import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import styles from './NativeVisuals.module.css';

export const PLUM = '#4C2365';
export const PLUM_MID = '#7A4E96';
export const PLUM_LIGHT = '#B49BC6';
export const TEAL = '#1F7A6C';
export const AMBER = '#B8860B';
export const RUST = '#C2410C';
export const GREEN = '#2F6B3E';
export const AXIS = '#6b7280';
export const GRID = '#e5e7eb';

// Enough hues to label a donut without repeating; ordered so the first few
// carry the most contrast, since most slices are small.
export const SLICE_COLOURS = [
  PLUM, TEAL, AMBER, PLUM_LIGHT, RUST, '#3B6EA5', '#7A4E96', '#8A8F98', '#5B9E6B'
];

export const pctTick = (value) => `${value}%`;

/** 1234567 -> ₹12.3L, which is how fees are read here. */
export const inr = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (Math.abs(n) >= 1000) return `₹${Math.round(n / 1000)}k`;
  return `₹${Math.round(n)}`;
};

export const monthLabel = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value ?? '');
  return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

export const Card = ({ title, note, wide, children }) => (
  <section className={wide ? `${styles.card} ${styles.cardWide}` : styles.card}>
    <h3 className={styles.cardTitle}>{title}</h3>
    {note ? <p className={styles.cardNote}>{note}</p> : null}
    <div className={styles.chartBox}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  </section>
);

export const LineCard = ({
  title, note, data, xKey, series, yTickFormatter, tooltipFormatter, wide, area
}) => (
  <Card title={title} note={note} wide={wide}>
    {area ? (
      <AreaChart data={data} margin={{ top: 6, right: 14, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey={xKey} tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={monthLabel} />
        <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={yTickFormatter} width={56} />
        <Tooltip formatter={tooltipFormatter} labelFormatter={monthLabel} />
        {series.length > 1 ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
        {series.map((s) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.colour}
            fill={s.colour}
            fillOpacity={0.14}
            strokeWidth={2}
          />
        ))}
      </AreaChart>
    ) : (
      <LineChart data={data} margin={{ top: 6, right: 14, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey={xKey} tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={monthLabel} />
        <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={yTickFormatter} width={48} />
        <Tooltip formatter={tooltipFormatter} labelFormatter={monthLabel} />
        {series.length > 1 ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
        {series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.colour}
            strokeWidth={2}
            dot={{ r: 2.5 }}
            activeDot={{ r: 4 }}
          />
        ))}
      </LineChart>
    )}
  </Card>
);

/** A pie when `donut` is false, a ring with the total in the middle when true. */
export const PieCard = ({
  title, note, data, nameKey = 'name', valueKey = 'value', donut = true,
  valueFormatter = (v) => String(v), centreLabel, centreValue, wide
}) => (
  <section className={wide ? `${styles.card} ${styles.cardWide}` : styles.card}>
    <h3 className={styles.cardTitle}>{title}</h3>
    {note ? <p className={styles.cardNote}>{note}</p> : null}
    <div className={styles.donutRow}>
      <div className={styles.donutWrap}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <Tooltip formatter={(value, name) => [valueFormatter(value), name]} />
          <Pie
            data={data}
            dataKey={valueKey}
            nameKey={nameKey}
            innerRadius={donut ? '58%' : 0}
            outerRadius="82%"
            paddingAngle={donut ? 1.5 : 0}
            stroke="#ffffff"
            strokeWidth={1}
          >
            {data.map((entry, index) => (
              <Cell key={entry[nameKey]} fill={SLICE_COLOURS[index % SLICE_COLOURS.length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      {donut && centreValue ? (
        <div className={styles.donutCentre} aria-hidden="true">
          <span className={styles.donutValue}>{centreValue}</span>
          {centreLabel ? <span className={styles.donutLabel}>{centreLabel}</span> : null}
          </div>
      ) : null}
    </div>
      <ul className={styles.legend}>
      {data.map((entry, index) => (
        <li key={entry[nameKey]}>
          <span
            className={styles.swatch}
            style={{ background: SLICE_COLOURS[index % SLICE_COLOURS.length] }}
          />
          <span className={styles.legendName}>{entry[nameKey]}</span>
          <span className={styles.legendValue}>{valueFormatter(entry[valueKey])}</span>
        </li>
      ))}
      </ul>
    </div>
  </section>
);

export const BarCard = ({
  title, note, data, xKey, series, layout = 'horizontal', yTickFormatter,
  tooltipFormatter, wide, categoryWidth = 130, angled, colourBy
}) => (
  <Card title={title} note={note} wide={wide}>
    <BarChart
      data={data}
      layout={layout}
      margin={{ top: 6, right: 16, bottom: 4, left: 0 }}
    >
      <CartesianGrid stroke={GRID} vertical={layout === 'vertical'} horizontal={layout !== 'vertical'} />
      {layout === 'vertical' ? (
        <>
          <XAxis type="number" tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={yTickFormatter} />
          <YAxis
            type="category"
            dataKey={xKey}
            tick={{ fill: AXIS, fontSize: 11 }}
            width={categoryWidth}
          />
        </>
      ) : (
        <>
          <XAxis
            dataKey={xKey}
            tick={{ fill: AXIS, fontSize: 11 }}
            interval={0}
            angle={angled ? -18 : 0}
            textAnchor={angled ? 'end' : 'middle'}
            height={angled ? 64 : 30}
          />
          <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={yTickFormatter} width={52} />
        </>
      )}
      <Tooltip formatter={tooltipFormatter} />
      {series.length > 1 ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
      {series.map((s) => (
        <Bar
          key={s.key}
          dataKey={s.key}
          name={s.name}
          fill={s.colour}
          stackId={s.stackId}
          radius={layout === 'vertical' ? [0, 3, 3, 0] : [3, 3, 0, 0]}
        >
          {colourBy
            ? data.map((row) => <Cell key={row[xKey]} fill={colourBy(row)} />)
            : null}
        </Bar>
      ))}
    </BarChart>
  </Card>
);

/**
 * Recharts has no heat map, so this is a plain grid. Cells carry the value as
 * text as well as colour — colour alone is unreadable for anyone who cannot
 * separate the hues, and hard to read precisely for everyone else.
 */
export const HeatMapCard = ({
  title, note, rows, columns, valueAt, formatValue = (v) => v, wide = true, unit = ''
}) => {
  const values = [];
  rows.forEach((r) => columns.forEach((c) => {
    const v = valueAt(r, c);
    if (v !== null && v !== undefined) values.push(v);
  }));
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const span = max - min || 1;

  return (
    <section className={wide ? `${styles.card} ${styles.cardWide}` : styles.card}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {note ? <p className={styles.cardNote}>{note}</p> : null}
      <div className={styles.heatScroll}>
        <table className={styles.heat}>
          <thead>
            <tr>
              <th scope="col" className={styles.heatCorner} />
              {columns.map((c) => (
                <th key={c} scope="col">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r}>
                <th scope="row">{r}</th>
                {columns.map((c) => {
                  const v = valueAt(r, c);
                  if (v === null || v === undefined) {
                    return <td key={c} className={styles.heatEmpty} title="No assessments">—</td>;
                  }
                  const t = (v - min) / span;
                  return (
                    <td
                      key={c}
                      style={{
                        background: `rgba(76, 35, 101, ${0.08 + t * 0.82})`,
                        color: t > 0.55 ? '#ffffff' : '#1f2937'
                      }}
                      title={`${r} · ${c}: ${formatValue(v)}${unit}`}
                    >
                      {formatValue(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={styles.heatKey}>
        <span className={styles.heatKeyBar} aria-hidden="true" />
        <span>
          {formatValue(min)}{unit} to {formatValue(max)}{unit} · a dash means nothing recorded
        </span>
      </p>
    </section>
  );
};
